/**
 * Projects-V3 read-only stores: Processes,
 * ProjectActivity, ProjectComments. Each is keyed by workspace slug (or
 * project id for project-scoped collections) and follows the same shallow
 * cache pattern used elsewhere in the codebase.
 */

import { makeAutoObservable } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";

import { ProcessesService } from "@/services/projects-v3.service";
import type {
  IProcessCreatePayload,
  IProcessStagePayload,
  TProcessUpdatePayload,
} from "@/services/projects-v3.service";
import type { TProcess, TProcessStage } from "@/types/project";

import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import { peekQueryClient } from "@/queries/client";
import { processBankQuery, processesKey } from "@/queries/processes";

export class ProcessesStore {
  // The process lists (the bank, the pickable list, usage and the settings
  // table's pages) are queries (`app/queries/processes.ts`), read through
  // `hooks/use-processes.ts`. What is left here is the writers.
  private service: ProcessesService;

  private rootStore: RootStore;

  constructor(rootStore: RootStore, service?: ProcessesService) {
    this.rootStore = rootStore;
    this.service = service ?? new ProcessesService();
    makeAutoObservable(
      this,
      {
        rootStore: false,
      } as never,
      {
        autoBind: true,
      },
    );
  }

  // --- Writes (NG-633) --------------------------------------------------
  // Every write re-reads the process lists rather than patching from its own
  // response: a stage write answers with a stage, a reorder renumbers rows it
  // was never asked about, a status change can take a row out of a filtered
  // table, and only the server knows where a row sorts. Every reader has to
  // agree afterwards.

  /**
   * After a confirmed write, mark every process list stale under one prefix:
   * the bank, the pickable list, usage and the settings table's pages. Each
   * one on screen re-reads at once, each one off screen on its next mount.
   * Resolves when the ones on screen have re-read, so the writer resolves over
   * the saved row, and never rejects: a failed refresh must not read as a
   * failed write, which already committed, so it warns. Nothing to do before
   * the session resolves, when nothing is cached under any key.
   */
  private async refreshAfterWrite(slug: string, processId: string, reason: string) {
    const scope = queryCacheScope(slug, "ProcessesStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    await client.invalidateQueries({ queryKey: processesKey(scope) }).catch((error: unknown) => {
      console.warn(`[ProcessesStore] post-${reason} refresh failed`, {
        slug,
        processId,
        error,
      });
    });
  }

  /** The bank as it stood before a write, for its analytics diff: the cached
   *  one, or empty if no reader has loaded it. */
  private bankBeforeWrite(slug: string): TProcess[] {
    const scope = queryCacheScope(slug, "ProcessesStore");
    const cached = scope
      ? peekQueryClient()?.getQueryData(processBankQuery(this.service, scope).queryKey)
      : undefined;
    return cached ?? [];
  }

  async createProcess(
    workspaceSlug: string,
    payload: IProcessCreatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const created = await this.service.create(workspaceSlug, payload);
    // Before the refresh, so a failed reload can't drop it.
    track("process_created", {
      process_id: created.id,
      stage_count: created.stages?.length ?? 0,
      source,
    });
    await this.refreshAfterWrite(workspaceSlug, created.id, "create");
    return created;
  }

  async updateProcess(
    workspaceSlug: string,
    processId: string,
    payload: TProcessUpdatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const before = this.processById(workspaceSlug, processId);
    const updated = await this.service.update(workspaceSlug, processId, payload);
    this.trackProcessUpdate(processId, payload, before, source);
    await this.refreshAfterWrite(workspaceSlug, processId, "update");
    return updated;
  }

  async deleteProcess(
    workspaceSlug: string,
    processId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.remove(workspaceSlug, processId);
    track("process_deleted", { process_id: processId, source });
    await this.refreshAfterWrite(workspaceSlug, processId, "delete");
  }

  private processById(workspaceSlug: string, processId: string): TProcess | undefined {
    return this.bankBeforeWrite(workspaceSlug).find((p) => p.id === processId);
  }

  /** The Active switch sends `is_active` alone: an activation, not an edit. */
  private trackProcessUpdate(
    processId: string,
    payload: TProcessUpdatePayload,
    before: TProcess | undefined,
    source: AnalyticsSource,
  ): void {
    const keys = Object.keys(payload);
    if (keys.length === 1 && payload.is_active !== undefined) {
      track(payload.is_active ? "process_activated" : "process_deactivated", {
        process_id: processId,
        source,
      });
      return;
    }
    // The record holds its teams as objects; the payload sends their ids.
    const stored = before && { ...before, team_access: before.team_access.map((team) => team.id) };
    track("process_updated", {
      process_id: processId,
      fields: changedFields(payload, stored),
      source,
    });
  }

  async addStage(
    workspaceSlug: string,
    processId: string,
    payload: IProcessStagePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    const stage = await this.service.addStage(workspaceSlug, processId, payload);
    track("process_stage_created", { process_id: processId, stage_id: stage.id, source });
    await this.refreshAfterWrite(workspaceSlug, processId, "stage create");
    return stage;
  }

  async updateStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    payload: Partial<IProcessStagePayload>,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    const before = this.processById(workspaceSlug, processId)?.stages.find((s) => s.id === stageId);
    const stage = await this.service.updateStage(workspaceSlug, processId, stageId, payload);
    const ids = { process_id: processId, stage_id: stageId, source };
    // The stage's Active switch sends `is_active` alone: an activation, not an edit.
    if (Object.keys(payload).length === 1 && payload.is_active !== undefined) {
      track(payload.is_active ? "process_stage_activated" : "process_stage_deactivated", ids);
    } else {
      track("process_stage_updated", { ...ids, fields: changedFields(payload, before) });
    }
    await this.refreshAfterWrite(workspaceSlug, processId, "stage update");
    return stage;
  }

  /** Delete a stage, first moving its live work items to `moveTo` (NG-635).
   *  Omitting `moveTo` on a stage that holds anything is a 409 carrying the
   *  candidates — the caller turns that into the move dialog and retries. */
  async deleteStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    moveTo?: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.service.removeStage(workspaceSlug, processId, stageId, moveTo);
    track("process_stage_deleted", {
      process_id: processId,
      stage_id: stageId,
      moved_work_items: moveTo !== undefined,
      source,
    });
    await this.refreshAfterWrite(workspaceSlug, processId, "stage delete");
  }

  /** Retire a process, or bring it back (NG-635). Status is the one field the
   *  settings table also FILTERS on: under an Active filter, the row a person
   *  just retired must leave the table rather than stay on wearing an Inactive
   *  badge. The re-read every write ends with is what drops it, with the count
   *  and the cursors, and an unfiltered table keeps every page it had loaded. */
  async setProcessActive(
    workspaceSlug: string,
    processId: string,
    isActive: boolean,
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    return this.updateProcess(workspaceSlug, processId, { is_active: isActive }, source);
  }

  /** Retire a stage, or bring it back (NG-635). The work items in it do not
   *  move; the server re-derives every project's current stage in the same
   *  transaction, which is why this re-syncs the bank like any other write. */
  async setStageActive(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    isActive: boolean,
    source: AnalyticsSource = "page",
  ): Promise<TProcessStage> {
    return this.updateStage(workspaceSlug, processId, stageId, { is_active: isActive }, source);
  }

  /** Persist a whole stage ordering. `stageIds` must be every active stage of
   *  the process, exactly once — the server rejects a partial list rather than
   *  guessing where the omitted stages belong. */
  async reorderStages(
    workspaceSlug: string,
    processId: string,
    stageIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TProcess> {
    const process = await this.service.reorderStages(workspaceSlug, processId, stageIds);
    track("process_stages_reordered", { process_id: processId, count: stageIds.length, source });
    await this.refreshAfterWrite(workspaceSlug, processId, "reorder");
    return process;
  }
}
